import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Stethoscope, User, Users2, Calendar, Pill, DollarSign, LayoutDashboard, AlertTriangle, HeartHandshake, Sparkles, ShieldCheck, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

const roleIcon = { doctor: Stethoscope, patient: User, caregiver: Users2, admin: ShieldCheck };
const roleLabel = {
    doctor: "Médico(a) · Admin", patient: "Paciente", caregiver: "Cuidador", admin: "Administrador",
    nurse: "Enfermeiro(a)", physiotherapist: "Fisioterapeuta", nutritionist: "Nutricionista",
    psychologist: "Psicólogo(a)", social_worker: "Assistente Social",
    occupational_therapist: "Terapeuta Ocupacional", pharmacist: "Farmacêutico(a)",
    transpersonal_therapist: "Terapeuta Transpessoal",
};

const PROFESSIONAL_ROLES = new Set([
    "doctor", "nurse", "physiotherapist", "occupational_therapist", "nutritionist",
    "psychologist", "social_worker", "pharmacist", "transpersonal_therapist", "admin",
]);

// Base nav shown to every approved professional (doctor, nurse, physio, nutri, psy, etc.)
const PROFESSIONAL_NAV = [
    { to: "/dashboard", label: "Painel", Icon: LayoutDashboard },
    { to: "/patients", label: "Pacientes", Icon: Users },
    { to: "/calendar", label: "Agenda", Icon: Calendar },
    { to: "/monitoring", label: "Risco", Icon: AlertTriangle },
    { to: "/caregiver-monitoring", label: "Cuidador", Icon: HeartHandshake },
    { to: "/reminders", label: "Lembretes", Icon: Pill },
];

// Extra nav restricted to doctor/admin (financial, content, team management)
const DOCTOR_EXTRA_NAV = [
    { to: "/billing", label: "Faturamento", Icon: DollarSign },
    { to: "/admin/content", label: "Conteúdo", Icon: Sparkles, badgeKey: "content" },
    { to: "/admin/users", label: "Equipe", Icon: ShieldCheck, badgeKey: "pending" },
];

export default function AppShell({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const Icon = roleIcon[user?.role] || User;
    const isDoctor = user?.role === "doctor" || user?.role === "admin";
    const isProfessional = user && PROFESSIONAL_ROLES.has(user.role);
    // Unified doctor+admin: doctors have full global access (patients, scheduling
    // across professionals, admin tools). Legacy `admin` accounts (if any) also
    // keep the same navigation.
    const isAdmin = isDoctor;
    const [modCount, setModCount] = useState(0);
    const [pendingUsersCount, setPendingUsersCount] = useState(0);

    useEffect(() => {
        if (!isDoctor) return;
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/moderation/count");
                if (alive) setModCount(data.count || 0);
            } catch (e) { console.warn("AppShell: moderation count polling failed", e); }
        };
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [isDoctor, location.pathname]);

    useEffect(() => {
        if (!isAdmin) return;
        let alive = true;
        const load = async () => {
            try {
                const { data } = await api.get("/admin/users/stats");
                if (alive) setPendingUsersCount(data.pending || 0);
            } catch (e) { console.warn("AppShell: pending users polling failed", e); }
        };
        load();
        const t = setInterval(load, 60000);
        return () => { alive = false; clearInterval(t); };
    }, [isAdmin, location.pathname]);

    const navItems = isProfessional
        ? (isDoctor ? [...PROFESSIONAL_NAV, ...DOCTOR_EXTRA_NAV] : PROFESSIONAL_NAV)
        : [];
    const badges = { content: modCount, pending: pendingUsersCount };

    return (
        <div className="min-h-screen bg-base">
            <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[rgba(30,63,51,0.1)]">
                <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16 gap-4">
                    <Link to="/dashboard" data-testid={TID.nav.brand} className="flex items-center gap-3 text-brand font-display font-semibold text-lg shrink-0">
                        <span className="w-9 h-9 rounded-xl bg-brand text-white flex items-center justify-center">
                            <Stethoscope size={18} />
                        </span>
                        <span className="hidden sm:block">Clínica Fernandes</span>
                    </Link>
                    {navItems.length > 0 && (
                        <nav className="hidden md:flex items-center gap-1 flex-1 justify-center">
                            {navItems.map((n) => {
                                const active = location.pathname === n.to || (n.to !== "/dashboard" && location.pathname.startsWith(n.to));
                                const N = n.Icon;
                                const badgeVal = n.badgeKey ? (badges[n.badgeKey] || 0) : 0;
                                const showBadge = badgeVal > 0;
                                return (
                                    <Link
                                        key={n.to}
                                        to={n.to}
                                        data-testid={`nav-${n.to.replace(/\//g, "-").replace(/^-/, "")}`}
                                        className={`text-sm px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors relative ${active ? "bg-brand text-white" : "text-brand hover:bg-alt"}`}
                                    >
                                        <N size={14} /> {n.label}
                                        {showBadge && (
                                            <span data-testid={`nav-badge-${n.badgeKey}`} className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--status-danger)] text-white text-[10px] font-semibold flex items-center justify-center">
                                                {badgeVal}
                                            </span>
                                        )}
                                    </Link>
                                );
                            })}
                        </nav>
                    )}
                    <div className="flex items-center gap-3 shrink-0">
                        {user?.role === "doctor" && <NotificationBell />}
                        <div data-testid={TID.nav.userMenu} className="hidden lg:flex items-center gap-2 text-sm text-[var(--text-secondary)] px-3 py-1.5 rounded-full bg-alt">
                            <Icon size={14} className="text-brand" />
                            <span className="font-medium">{user?.name}</span>
                            <span className="text-muted-clinical">•</span>
                            <span>{roleLabel[user?.role]}</span>
                        </div>
                        <button
                            data-testid={TID.nav.logoutBtn}
                            onClick={async () => { await logout(); navigate("/"); }}
                            className="btn-ghost text-sm"
                        >
                            <LogOut size={16} /> Sair
                        </button>
                    </div>
                </div>
            </header>
            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">{children}</main>
        </div>
    );
}
